import { useEffect, useRef } from 'react';
import { toPlainDocumentTitleSegment } from '@/lib/html-plain-text';

const TITLE_SUFFIX = ' | LyrraOne';
const TITLE_DEFAULT = 'LyrraOne';

function markTitleElementNotForMachineTranslate() {
  document.querySelector('title')?.setAttribute('translate', 'no');
}

/** Chrome « Traduire la page » peut réécrire &lt;title&gt; avec des nœuds &lt;font&gt; — le texte de l’onglet affiche alors des balises. */
function titleLooksLikeMarkup(s: string): boolean {
  return /<[a-zA-Z!?/]/.test(s);
}

/**
 * `document.title` est toujours du texte brut : pas de rendu HTML.
 * Titres rich-text, entités HTML, et réécritures du navigateur après traduction automatique.
 */
export function usePageTitle(title: string) {
  const lastSanitizedFullTitle = useRef(TITLE_DEFAULT);

  useEffect(() => {
    markTitleElementNotForMachineTranslate();
  }, []);

  useEffect(() => {
    const previousTitle = document.title;
    const plain = toPlainDocumentTitleSegment(String(title));
    const next = plain ? `${plain}${TITLE_SUFFIX}` : TITLE_DEFAULT;
    lastSanitizedFullTitle.current = next;
    markTitleElementNotForMachineTranslate();
    document.title = next;
    return () => {
      document.title = previousTitle;
    };
  }, [title]);

  useEffect(() => {
    markTitleElementNotForMachineTranslate();
    const el = document.querySelector('title');
    if (!el) return;
    const obs = new MutationObserver(() => {
      const current = document.title;
      if (!titleLooksLikeMarkup(current)) return;
      const fix = lastSanitizedFullTitle.current;
      if (document.title !== fix) {
        document.title = fix;
        markTitleElementNotForMachineTranslate();
      }
    });
    obs.observe(el, { childList: true, subtree: true, characterData: true });
    return () => obs.disconnect();
  }, []);
}
